import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
    readCookieConsent,
    persistCookieConsent,
    HM_CONSENT_UPDATED_EVENT,
    HM_OPEN_COOKIE_PREFERENCES_EVENT,
    revokeGoogleTagConsent,
    revokeMetaPixelConsent,
} from '../utils/cookieConsent';

const CookieConsentContext = createContext(null);

export function useCookieConsent() {
    return useContext(CookieConsentContext);
}

export function CookieConsentProvider({ children }) {
    const [consent, setConsent] = useState(() => readCookieConsent());
    const [preferencesOpen, setPreferencesOpen] = useState(false);

    const refresh = useCallback(() => {
        setConsent(readCookieConsent());
    }, []);

    React.useEffect(() => {
        const onUpdate = () => refresh();
        window.addEventListener(HM_CONSENT_UPDATED_EVENT, onUpdate);
        return () => window.removeEventListener(HM_CONSENT_UPDATED_EVENT, onUpdate);
    }, [refresh]);

    React.useEffect(() => {
        const onOpen = () => setPreferencesOpen(true);
        window.addEventListener(HM_OPEN_COOKIE_PREFERENCES_EVENT, onOpen);
        return () => window.removeEventListener(HM_OPEN_COOKIE_PREFERENCES_EVENT, onOpen);
    }, []);

    const savePreferences = useCallback((next) => {
        const prev = readCookieConsent();
        if (prev?.analytics && !next.analytics) {
            revokeGoogleTagConsent();
        }
        if (prev?.marketing && !next.marketing) {
            revokeMetaPixelConsent();
        }
        persistCookieConsent(next);
        refresh();
        setPreferencesOpen(false);
        const shouldReloadFb = !!(prev?.marketing && !next.marketing && typeof window !== 'undefined' && window.fbq);
        if (shouldReloadFb) {
            window.location.reload();
        }
    }, [refresh]);

    const acceptAll = useCallback(() => {
        savePreferences({ analytics: true, marketing: true });
    }, [savePreferences]);

    const rejectNonEssential = useCallback(() => {
        savePreferences({ analytics: false, marketing: false });
    }, [savePreferences]);

    const openPreferences = useCallback(() => setPreferencesOpen(true), []);
    const closePreferences = useCallback(() => setPreferencesOpen(false), []);

    const value = useMemo(() => ({
        consent,
        decided: consent != null,
        preferencesOpen,
        setPreferencesOpen,
        acceptAll,
        rejectNonEssential,
        savePreferences,
        openPreferences,
        closePreferences,
    }), [
        consent,
        preferencesOpen,
        acceptAll,
        rejectNonEssential,
        savePreferences,
        openPreferences,
        closePreferences,
    ]);

    return (
        <CookieConsentContext.Provider value={value}>
            {children}
        </CookieConsentContext.Provider>
    );
}
